import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { Incident } from '../Types/Incident';
import { getIncidentDetail } from '../Services/IncidentService';
import AppBreadcrumbs from '../Components/Navigation/AppBreadcrumbs';
import IncidentInfoBar from '../Components/Incidents/IncidentInfoBar';
import { appColors } from '../Config/Theme';
import { useSecurity } from '../Contexts/SecurityProvider';

const IncidentReportPage: React.FC = () => {
  const { incidentId } = useParams<{ incidentId: string }>();
  const navigate = useNavigate();
  const [incident, setIncident] = useState<Incident | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({ open: false, message: '', severity: 'success' });
  const { hasAnyRole } = useSecurity();

  const canEditDetails = incident?.capabilities?.canEditDetails === true
    || hasAnyRole(['Admin', 'IncidentCommander']);

  const loadData = useCallback(async () => {
    if (!incidentId) return;
    setLoading(true);
    setError(null);
    try {
      const detail = await getIncidentDetail(incidentId);
      setIncident(detail);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load incident.');
    } finally {
      setLoading(false);
    }
  }, [incidentId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 12 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
        <Alert
          severity="error"
          action={<Button size="small" onClick={loadData}>Retry</Button>}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 1, px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, maxWidth: 1200, mx: 'auto', width: '100%' }}>
      {/* ── Breadcrumbs ── */}
      <AppBreadcrumbs items={[
        { label: 'Reports', path: '/reports' },
        { label: incident?.incidentName || 'Incident Report' },
      ]} />

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="text"
            size="small"
            startIcon={<BackIcon />}
            onClick={() => navigate('/reports')}
            sx={{ color: appColors.text.body }}
          >
            Back to Reports
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: appColors.text.heading }}>
          Incident Report
        </Typography>
        {canEditDetails && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/incidents/${incidentId}/edit`)}
            sx={{ fontWeight: 600 }}
          >
            Edit Details
          </Button>
        )}
      </Box>

      {/* ── Incident Info Bar ── */}
      {incident && (
        <IncidentInfoBar
          incidentName={incident.incidentName}
          incidentType={incident.incidentType}
          location={incident.location}
          startedAtUtc={incident.startedAtUtc}
          status={incident.status}
        />
      )}

      {/* ── Summary metrics ── */}
      {incident?.currentCounts && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          {[
            { label: 'Total People', value: incident.currentCounts.total, color: appColors.primary.main },
            { label: 'Accounted', value: incident.currentCounts.accounted, color: appColors.status.success },
            { label: 'Not Accounted', value: incident.currentCounts.notAccounted, color: appColors.status.error },
            { label: '% Complete', value: `${Math.round(incident.currentCounts.percentComplete)}%`, color: appColors.primary.main },
          ].map((m) => (
            <Box
              key={m.label}
              sx={{
                flex: '1 1 0',
                minWidth: { xs: 'calc(50% - 8px)', sm: 120 },
                bgcolor: appColors.surface.card,
                borderRadius: '12px',
                border: `1px solid ${appColors.border.default}`,
                px: 2,
                py: 1.5,
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" sx={{ color: appColors.text.caption, fontWeight: 500 }}>
                {m.label}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: m.color, lineHeight: 1.3 }}>
                {m.value}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default IncidentReportPage;
